import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DeathPoolForm.css';
import './shared.css';
import Toast from './Toast';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function DeathPoolForm({ onBack, user }) {
  const [currentCelebrity, setCurrentCelebrity] = useState('');
  const [celebrities, setCelebrities] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCelebrity, setSelectedCelebrity] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchError, setSearchError] = useState('');
  const [previewCelebrity, setPreviewCelebrity] = useState(null);
  const [wikiPreview, setWikiPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    loadUserList();
  }, []);

  const loadUserList = async () => {
    try {
      setIsLoadingList(true);
      const response = await axios.get(`${config.API_BASE_URL}/my-list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCelebrities(response.data.celebrities || []);
    } catch (error) {
      setToastMessage('Error loading your list');
    } finally {
      setIsLoadingList(false);
    }
  };

  const checkCelebrity = async (id) => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/check-celebrity/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking celebrity:', error);
      return { isAlreadyPicked: false };
    }
  };

  const handleCelebritySubmit = async (e) => {
    e.preventDefault();
    if (!currentCelebrity.trim()) {
      setToastMessage('Please enter a celebrity name');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);
    
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/search-celebrity/${encodeURIComponent(currentCelebrity)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid response format:', response.data);
        setSearchError('Error processing search results');
        return;
      }

      if (response.data.length === 0) {
        setSearchError('No matching celebrities found');
      } else {
        setSearchResults(response.data);
        setSearchError('');
      }
    } catch (error) {
      console.error('Search error:', error);
      setToastMessage('Error searching for celebrity');
      setSearchError('Error performing search. Please wait a minute and try again, if you keep getting this error please let me know');
    } finally {
      setIsSearching(false);
    }
  };

  const handleCelebritySelection = async (celebrity) => {
    const checkResult = await checkCelebrity(celebrity.id);
    if (checkResult.isAlreadyPicked) {
      setToastMessage(`Sorry, ${checkResult.celebrityName} has already been chosen by ${checkResult.pickedBy}`);
      return;
    }

    if (celebrities.some(c => c.id === celebrity.id)) {
      setToastMessage('You have already picked this celebrity');
      return;
    }

    if (celebrities.length >= 10) {
      setToastMessage('You already have 10 celebrities in your list');
      return;
    }

    const newCelebrities = [...celebrities, celebrity];
    
    try {
      await axios.post(
        `${config.API_BASE_URL}/entries`,
        { celebrities: newCelebrities },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setCelebrities(newCelebrities);
      setCurrentCelebrity('');
      setSearchResults([]);
      setToastMessage('Celebrity added to your list');
    } catch (error) {
      setToastMessage('Error saving your list');
    }
  };

  const handleDeleteCelebrity = async (indexToDelete) => {
    const newCelebrities = celebrities.filter((_, index) => index !== indexToDelete);
    try {
      await axios.post(
        `${config.API_BASE_URL}/entries`,
        { celebrities: newCelebrities },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setCelebrities(newCelebrities);
      setToastMessage('Celebrity removed from your list');
    } catch (error) {
      setToastMessage('Error updating your list');
    }
  };

  const fetchWikiPreview = async (celeb) => {
    if (!celeb.wikipediaUrl) return;
    
    setIsLoadingPreview(true);
    try {
      const title = celeb.wikipediaUrl.split('/wiki/')[1];
      const response = await axios.get(`${config.API_BASE_URL}/wiki-preview/${title}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setWikiPreview(response.data);
    } catch (error) {
      console.error('Error fetching wiki preview:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePreviewClick = async (celeb) => {
    if (previewCelebrity?.id === celeb.id) {
      setPreviewCelebrity(null);
      setWikiPreview(null);
    } else {
      setPreviewCelebrity(celeb);
      await fetchWikiPreview(celeb);
    }
  };

  return (
    <div className="death-pool-form">
      <div className="menu-bar">
        <button className="menu-button" onClick={onBack}>Back to Home</button>
      </div>
      
      <h2>Manage Your Celebrity List</h2>
      
      {isLoadingList ? (
        <div className="loading-message">Loading your list...</div>
      ) : (
        <>
          <p>Selected {celebrities.length}/10 celebrities</p>
          {celebrities.length >= 10 ? (
            <div className="list-full-message">
              Your list is complete. To add a celebrity you'll need to remove one from your list first.
            </div>
          ) : (
            <form onSubmit={handleCelebritySubmit}>
              <div>
                <input
                  type="text"
                  value={currentCelebrity}
                  onChange={(e) => setCurrentCelebrity(e.target.value)}
                  placeholder="Search by name for a celebrity to add to your list"
                />
                <button type="submit" disabled={isSearching}>
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {searchError && <div className="search-error">{searchError}</div>}
            </form>
          )}
          
          {searchResults && searchResults.length > 0 && (
            <div className="search-results">
              <h3>Select a celebrity:</h3>
              
              {/* Living celebrities section */}
              {Array.isArray(searchResults) && searchResults.some(celeb => !celeb?.isDeceased) && (
                <div className="living-celebrities">
                  {searchResults
                    .filter(celeb => celeb && !celeb.isDeceased)
                    .map((celeb) => (
                      <div key={celeb.id}>
                        <div className="search-result-item">
                          <h4>{celeb.label}</h4>
                          {celeb.description && <p>{celeb.description}</p>}
                          <div className="result-actions">
                            {celeb.wikipediaUrl ? (
                              <button 
                                className="preview-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreviewClick(celeb);
                                }}
                              >
                                {previewCelebrity?.id === celeb.id ? 'Hide Details' : 'Show Details'}
                              </button>
                            ) : (
                              <button 
                                className="preview-button disabled"
                                disabled
                              >
                                No Details Available
                              </button>
                            )}
                            <button 
                              className={`select-button ${celebrities.some(c => c.id === celeb.id) ? 'disabled' : ''}`}
                              onClick={() => handleCelebritySelection(celeb)}
                              disabled={celebrities.some(c => c.id === celeb.id)}
                            >
                              {celebrities.some(c => c.id === celeb.id) 
                                ? 'Already In Your List'
                                : 'Add Celebrity to Your List'}
                            </button>
                          </div>
                        </div>
                        {previewCelebrity?.id === celeb.id && (
                          <div className="wiki-preview">
                            {isLoadingPreview ? (
                              <div className="preview-loading">Loading preview...</div>
                            ) : wikiPreview ? (
                              <>
                                {wikiPreview.image && (
                                  <div className="preview-image">
                                    <img src={wikiPreview.image} alt={celeb.label} />
                                  </div>
                                )}
                                <div className="preview-text">
                                  {wikiPreview.extract}
                                </div>
                              </>
                            ) : (
                              <div className="preview-error">No Wikipedia preview available</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
              
              {/* Deceased celebrities section */}
              {Array.isArray(searchResults) && searchResults.some(celeb => celeb?.isDeceased) && (
                <div className="deceased-celebrities">
                  {searchResults.some(celeb => !celeb?.isDeceased) && (
                    <div className="deceased-divider">Deceased Celebrities</div>
                  )}
                  {searchResults
                    .filter(celeb => celeb && celeb.isDeceased)
                    .map((celeb) => (
                      <div 
                        key={celeb.id} 
                        className="search-result-item deceased"
                      >
                        <h4>{celeb.label}</h4>
                        {celeb.description && <p>{celeb.description}</p>}
                        <div className="deceased-notice">
                          Deceased: {celeb.deathDate}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          
          {celebrities.length > 0 && (
            <div className="selected-celebrities">
              <h3>Your Selected Celebrities</h3>
              <ul>
                {celebrities.map((celeb, index) => (
                  <li key={index}>
                    <div className="celeb-number">#{index + 1}</div>
                    <button 
                      className="delete-button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCelebrity(index);
                      }}
                      aria-label="Delete celebrity"
                    >
                      ×
                    </button>
                    <div className="celeb-name">{celeb.label}</div>
                    {celeb.description && (
                      <div className="celeb-description">{celeb.description}</div>
                    )}
                    <div className="wiki-link">
                      {celeb.wikipediaUrl ? (
                        <a href={celeb.wikipediaUrl} target="_blank" rel="noopener noreferrer">
                          View Wikipedia Page →
                        </a>
                      ) : (
                        <span className="no-wiki">No Wikipedia page available</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {toastMessage && (
            <Toast 
              message={toastMessage} 
              onClose={() => setToastMessage('')}
            />
          )}
        </>
      )}
    </div>
  );
}

export default DeathPoolForm; 